import React, { useState, useEffect, useCallback, useRef } from 'react';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import { fetchArquivoDoc, saveArquivoDoc, fetchArquivosParaMencao } from '../../api/repositorios';
import { detectarMencaoArquivo, tokenMencaoArquivo } from './repoDocMencoes';
import RepoConteudoDoc from './RepoConteudoDoc';

function RepoArquivoDoc({ repoId, arquivo, onNavigateToArquivo, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [moduloIndisponivel, setModuloIndisponivel] = useState(false);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [resumo, setResumo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [meta, setMeta] = useState(null);
  const [mencoes, setMencoes] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [mencaoAberta, setMencaoAberta] = useState(false);
  const [mencaoInicio, setMencaoInicio] = useState(-1);
  const [mencaoAtiva, setMencaoAtiva] = useState(0);
  const textareaRef = useRef(null);

  const carregar = useCallback(async () => {
    if (!repoId || !arquivo?.id) return;
    setLoading(true);
    setErro('');
    setModuloIndisponivel(false);
    try {
      const data = await fetchArquivoDoc(repoId, arquivo.id);
      if (data?.moduloIndisponivel) {
        setModuloIndisponivel(true);
        return;
      }
      const doc = data?.doc;
      setResumo(doc?.resumo || '');
      setConteudo(doc?.conteudo || '');
      setMeta(doc ? { atualizado_em: doc.atualizado_em, atualizado_por_nome: doc.atualizado_por_nome } : null);
      setMencoes(Array.isArray(data?.mencoes) ? data.mencoes : []);
    } catch (e) {
      setErro(e?.response?.data?.error || 'Erro ao carregar documentação.');
    } finally {
      setLoading(false);
    }
  }, [repoId, arquivo?.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const buscarSugestoes = useCallback(async (query) => {
    if (!repoId) return;
    try {
      const lista = await fetchArquivosParaMencao(repoId, { q: query, excluir: arquivo?.id });
      setSugestoes(Array.isArray(lista) ? lista : []);
    } catch (_) {
      setSugestoes([]);
    }
  }, [repoId, arquivo?.id]);

  const onChangeConteudo = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart;
    setConteudo(val);
    const det = detectarMencaoArquivo(val, caret);
    if (det) {
      setMencaoInicio(det.inicio);
      setMencaoAberta(true);
      setMencaoAtiva(0);
      buscarSugestoes(det.query);
    } else {
      setMencaoAberta(false);
      setMencaoInicio(-1);
    }
  };

  const inserirMencao = (arq) => {
    if (!arq || mencaoInicio < 0) return;
    const ta = textareaRef.current;
    const caret = ta ? ta.selectionStart : conteudo.length;
    const antes = conteudo.slice(0, mencaoInicio);
    const depois = conteudo.slice(caret);
    const token = tokenMencaoArquivo(arq.id, arq.caminho);
    const novo = `${antes}${token}${depois}`;
    setConteudo(novo);
    setMencaoAberta(false);
    setMencaoInicio(-1);
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = antes.length + token.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const onKeyDownEditor = (e) => {
    if (!mencaoAberta || !sugestoes.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMencaoAtiva((i) => Math.min(i + 1, sugestoes.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMencaoAtiva((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      inserirMencao(sugestoes[mencaoAtiva]);
    } else if (e.key === 'Escape') {
      setMencaoAberta(false);
    }
  };

  const salvar = async () => {
    verificandoToken();
    setSalvando(true);
    setErro('');
    try {
      await saveArquivoDoc(repoId, arquivo.id, {
        resumo: (resumo || '').trim() || null,
        conteudo,
        refs: [],
      });
      setEditando(false);
      await carregar();
      if (onSaved) onSaved();
    } catch (e) {
      setErro(e?.response?.data?.error || 'Erro ao salvar documentação.');
    } finally {
      setSalvando(false);
    }
  };

  if (moduloIndisponivel) {
    return (
      <div className="alert alert-warning py-2 small mb-0">
        Documentação de arquivos ainda não instalada no banco. Execute os scripts <strong>071</strong> e <strong>072</strong> em <code>scripts/mysql/</code>.
      </div>
    );
  }

  if (loading) return <p className="text-muted small mb-0">Carregando documentação...</p>;

  return (
    <div className="repo-doc-panel">
      <div className="repo-doc-toolbar d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div className="small text-muted">
          {meta?.atualizado_em ? (
            <>Atualizado {new Date(meta.atualizado_em).toLocaleString('pt-BR')}{meta.atualizado_por_nome ? ` · ${meta.atualizado_por_nome}` : ''}</>
          ) : (
            'Ainda sem documentação'
          )}
        </div>
        <div className="d-flex gap-2">
          {editando ? (
            <>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={salvando} onClick={() => { setEditando(false); carregar(); }}>Cancelar</button>
              <button type="button" className="btn btn-sm btn-primary" disabled={salvando} onClick={salvar}>{salvando ? 'Salvando...' : 'Salvar'}</button>
            </>
          ) : (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditando(true)}>
              <i className="fas fa-pen me-1" />{meta ? 'Editar' : 'Documentar'}
            </button>
          )}
        </div>
      </div>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

      {editando ? (
        <>
          <div className="form-group mb-2">
            <label className="small fw-semibold">Resumo (1 linha)</label>
            <input
              type="text"
              className="form-control form-control-sm"
              maxLength={280}
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              placeholder="Ex.: Calcula desconto por faixa de quantidade"
            />
          </div>
          <div className="form-group mb-1 repo-doc-editor-wrap">
            <label className="small fw-semibold">Como funciona</label>
            <textarea
              ref={textareaRef}
              className="form-control form-control-sm repo-doc-textarea"
              rows={12}
              value={conteudo}
              onChange={onChangeConteudo}
              onKeyDown={onKeyDownEditor}
              placeholder="Descreva o arquivo. Digite @ para mencionar outro arquivo do repositório — ao clicar na menção, o leitor vai à documentação dele."
            />
            {mencaoAberta && sugestoes.length > 0 && (
              <ul className="repo-doc-mencao-lista">
                {sugestoes.map((s, idx) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`repo-doc-mencao-item ${idx === mencaoAtiva ? 'ativo' : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); inserirMencao(s); }}
                    >
                      <span className="repo-doc-mencao-item-path">{s.caminho}</span>
                      {s.doc_resumo && <span className="repo-doc-mencao-item-resumo">{s.doc_resumo}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <small className="text-muted d-block mt-1">Use <strong>@</strong> para referenciar outro arquivo. A menção aparece em azul e leva à documentação dele.</small>
          </div>
        </>
      ) : (
        <>
          {resumo && <p className="repo-doc-resumo">{resumo}</p>}
          <RepoConteudoDoc
            conteudo={conteudo}
            mencoes={mencoes}
            onClickMencao={(arquivoId) => onNavigateToArquivo && onNavigateToArquivo(arquivoId)}
          />
        </>
      )}
    </div>
  );
}

export default RepoArquivoDoc;
